import { getDashboardData } from "@stryker/core/api/dashboard";
import { QueryKeys } from "@stryker/core/queryKeys";
import { useQuery } from "@tanstack/react-query";
import { sub } from "date-fns";
import { useState } from "react";
import { omitBy } from "remeda";
import CostTables from "@/components/dashboard/CostTables";
import EngCost from "@/components/dashboard/EngCost";
import Filters from "@/components/dashboard/Filters";
import JobCountTables from "@/components/dashboard/JobCountTables";
import JobsCount from "@/components/dashboard/JobsCount";
import KitsCount from "@/components/dashboard/KitsCount";
import QuotesAndRevenues from "@/components/dashboard/QuotesAndRevenues";
import RevenueTables from "@/components/dashboard/RevenueTables";
import Summary from "@/components/dashboard/Summary";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Dashboard() {
  const [fromDate, setFromDate] = useState<Date | undefined>(sub(new Date(), { months: 1 }));
  const [toDate, setToDate] = useState<Date | undefined>(new Date());
  const [selectedFlag, setSelectedFlag] = useState<string>("");
  const [selectedBranch, setSelectedBranch] = useState<string>("");
  const [selectedPlant, setSelectedPlant] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedRepairSystem, setSelectedRepairSystem] = useState<string>("");
  const [selectedKitSize, setSelectedKitSize] = useState<string>("");

  const params = {
    start: fromDate,
    end: toDate,
    flag: selectedFlag,
    branch: selectedBranch,
    user: selectedUser,
    repair_system: selectedRepairSystem,
    kit_size: selectedKitSize,
    plant: selectedPlant,
  };
  const filteredParams = omitBy(params, (v) => !v || v === "''");

  const { data: graphsData } = useQuery({
    queryKey: [
      QueryKeys.Dashboard,
      fromDate,
      toDate,
      selectedFlag,
      selectedBranch,
      selectedPlant,
      selectedUser,
      selectedRepairSystem,
      selectedKitSize,
    ],
    queryFn: () => getDashboardData(filteredParams),
  });

  return (
    <div className="mb-8">
      <Filters
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        selectedFlag={selectedFlag}
        setSelectedFlag={setSelectedFlag}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        selectedPlant={selectedPlant}
        setSelectedPlant={setSelectedPlant}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        selectedRepairSystem={selectedRepairSystem}
        setSelectedRepairSystem={setSelectedRepairSystem}
        selectedKitSize={selectedKitSize}
        setSelectedKitSize={setSelectedKitSize}
      />
      {graphsData?.summary && <Summary summary={graphsData.summary} />}

      <Tabs defaultValue="jobs-count" className="overflow-hidden">
        <ScrollArea className="whitespace-nowrap rounded-md border">
          <TabsList className="flex w-max">
            <TabsTrigger value="jobs-count">Jobs count</TabsTrigger>
            <TabsTrigger value="kits-count">Kits count</TabsTrigger>
            <TabsTrigger value="quotes-revenues">Quotes/Revenues</TabsTrigger>
            <TabsTrigger value="cost">Eng Cost</TabsTrigger>
            <TabsTrigger value="job-pie-chart">Job count %</TabsTrigger>
            <TabsTrigger value="cost-pie-chart">Cost %</TabsTrigger>
            <TabsTrigger value="revenue-pie-chart">Revenue %</TabsTrigger>
          </TabsList>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
        <TabsContent value="jobs-count">
          <JobsCount graphsData={graphsData?.jobs} />
        </TabsContent>
        <TabsContent value="kits-count">
          <KitsCount graphsData={graphsData?.kits} />
        </TabsContent>
        <TabsContent value="quotes-revenues">
          <QuotesAndRevenues graphsData={graphsData?.revenuesCost} />
        </TabsContent>
        <TabsContent value="cost">
          <EngCost graphsData={graphsData?.cost} />
        </TabsContent>
        <TabsContent value="job-pie-chart">
          <JobCountTables graphsData={graphsData?.jobPieChart} />
        </TabsContent>
        <TabsContent value="cost-pie-chart">
          <CostTables graphsData={graphsData?.costPieChart} />
        </TabsContent>
        <TabsContent value="revenue-pie-chart">
          <RevenueTables graphsData={graphsData?.revenuePieChart} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
